import { Box, GlobalStyles, useTheme } from "@mui/material";
import { Fragment } from "react";
import { CircleMarker, Pane, Tooltip } from "react-leaflet";
import { useIsMobile } from "@/hooks/useIsMobile";
import { INNER_RADIUS } from "@/features/reassistRequests/utils/tokens";
import { type SalePin } from "../types";
import { displayName } from "../utils/format";
import {
  SALE_PIN_HIT_RADIUS,
  SALE_PIN_LABEL_CLASS,
  SALE_PIN_LABEL_MIN_ZOOM,
  SALE_PIN_PANE,
  SALE_PIN_RADIUS,
  salePinStyle,
} from "../utils/saleTokens";

interface Props {
  pins: SalePin[];
  onSelect: (id: number) => void;
  /** 地図のズーム。SALE_PIN_LABEL_MIN_ZOOM 以上で、物件名のラベルを常に出す。 */
  zoom: number;
}

/** 常に出すラベルの位置。ピンの右に、円と重ならない距離だけ離す。 */
const LABEL_OFFSET: [number, number] = [SALE_PIN_RADIUS + 2, 0];

/**
 * 販売中の資料があるピン。購入済みを奥、未購入を手前に描き、押すと物件の詳細を開く。
 * 検索結果のピンに隠れないよう専用の pane に描き、拡大したときは物件名のラベルを常に出す。
 */
export const SalePinMarkers: React.FC<Props> = ({ pins, onSelect, zoom }) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const hitRadius = isMobile ? SALE_PIN_HIT_RADIUS.sp : SALE_PIN_HIT_RADIUS.pc;
  const showLabels = zoom >= SALE_PIN_LABEL_MIN_ZOOM;
  const sorted = [...pins].sort(
    (a, b) => Number(b.isPurchased) - Number(a.isPurchased)
  );
  return (
    <>
      {/* ラベルは Leaflet が描く要素なので、class 名で見た目を当てる（白地・枠・小さい文字） */}
      <GlobalStyles
        styles={{
          [`.leaflet-tooltip.${SALE_PIN_LABEL_CLASS}`]: {
            padding: "2px 6px",
            fontSize: 12,
            lineHeight: 1.4,
            fontWeight: 700,
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.white.main,
            border: `1px solid ${theme.palette.border.main}`,
            borderRadius: INNER_RADIUS,
            boxShadow: "none",
            whiteSpace: "nowrap",
          },
          [`.leaflet-tooltip-right.${SALE_PIN_LABEL_CLASS}::before`]: {
            borderRightColor: theme.palette.border.main,
          },
        }}
      />
      <Pane name={SALE_PIN_PANE.name} style={{ zIndex: SALE_PIN_PANE.zIndex }}>
        {sorted.map((pin) => {
          const center: [number, number] = [
            Number(pin.latitude),
            Number(pin.longitude),
          ];
          const { radius, ...visible } = salePinStyle(theme, pin.isPurchased);
          // ピンの一覧 API は住所を返さないため、名称の無い物件はラベルを出さない（ホバーの案内は出す）
          const label = pin.buildingName.trim();
          const permanent = showLabels && label !== "";
          return (
            <Fragment key={pin.id}>
              <CircleMarker
                center={center}
                radius={radius}
                pathOptions={{ ...visible, interactive: false }}
              />
              {/* 透明な当たり判定。クリックと Tooltip はこちらが受ける。地図本体のクリック（地番検索）には伝えない。 */}
              <CircleMarker
                center={center}
                radius={hitRadius}
                pathOptions={{
                  stroke: false,
                  fill: true,
                  fillOpacity: 0,
                  bubblingMouseEvents: false,
                }}
                eventHandlers={{
                  click: () => {
                    onSelect(pin.id);
                  },
                }}
              >
                {permanent ? (
                  // permanent は作成時にしか効かないため、切り替えで作り直す（key）
                  <Tooltip
                    key="label"
                    permanent
                    direction="right"
                    offset={LABEL_OFFSET}
                    className={SALE_PIN_LABEL_CLASS}
                  >
                    {label}
                  </Tooltip>
                ) : (
                  <Tooltip key="hover">
                    <Box component="span" sx={{ fontSize: 13 }}>
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        {displayName({
                          buildingName: pin.buildingName,
                          address: "",
                        })}
                      </Box>
                      {pin.isPurchased ? "（購入済み）" : "（未購入）"}
                    </Box>
                  </Tooltip>
                )}
              </CircleMarker>
            </Fragment>
          );
        })}
      </Pane>
    </>
  );
};
